import React, { useMemo } from 'react'
import { IFilterByOption } from '../ui-components/InputSearchFilter'
import { usePharmaciesQuery } from '../graphql/hooks/pharmacies'
import { Pharmacy } from '../types/pharmacy'

type FilterLogic = 'AND' | 'OR'
type FilterCriterion =
  | 'HAS_APPOINTMENT'
  | 'OPEN_POTENTIAL'
  | 'CALLCENTER_AVAILABLE'
  | 'ONLY_PRIO'
  | 'WITH_REJECTIONS'

export type Filters = {
  selectedProjects: string[]
  filterByOption: IFilterByOption
  searchTerm?: string
  onlyWithScheduledAppointments?: boolean
  includesTerminatedProjects?: boolean
  isAvailableForCallcenter?: boolean
  isOnlyPriority?: boolean
  isOpenPotential?: boolean
  isWithDeleted?: boolean  
}

const toLogic = (opt: IFilterByOption): FilterLogic =>
  opt === IFilterByOption.AND ? 'AND' : 'OR'

const toCriteria = (f: Filters): FilterCriterion[] => {
  const c: FilterCriterion[] = []
  if (f.onlyWithScheduledAppointments) c.push('HAS_APPOINTMENT')
  if (f.isOpenPotential) c.push('OPEN_POTENTIAL')
  if (f.isAvailableForCallcenter) c.push('CALLCENTER_AVAILABLE')
  if (f.isOnlyPriority) c.push('ONLY_PRIO')
  if (f.includesTerminatedProjects) c.push('WITH_REJECTIONS')
  return c
}

export function usePharmacyMapData(filters: Filters) {
  const variables = useMemo(
    () => ({
      projectIds: filters.selectedProjects.length ? filters.selectedProjects : undefined,
      criteria: toCriteria(filters),
      logic: toLogic(filters.filterByOption),
      withDeleted: filters.isWithDeleted ?? false, 
    }),
    [filters]
  )

  const { data, error } = usePharmaciesQuery(variables)

  type Cluster = { id: string, pharmacies?: Pharmacy[] }

  const clusters = React.useMemo<Cluster[]>(
    () => (data ?? []),
    [data]
  )

  const pharmacies = React.useMemo(
    () => clusters.flatMap((c) => c?.pharmacies ?? []),
    [clusters]
  )

  const searched = useMemo(() => {
    const term = filters.searchTerm?.trim().toLowerCase()
    if (!term) return pharmacies
    return pharmacies.filter((p) => {
      const name = p?.name?.toLowerCase() ?? ''
      const okid = (p as any)?.okid?.toLowerCase?.() ?? ''
      const idMatch = Number(p?.id) === Number(term)
      return name.includes(term) || okid.includes(term) || idMatch
    })
  }, [pharmacies, filters.searchTerm])

  return { clusters, pharmacies: searched, error }
}
